import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { TeamSurveySummary } from '../../../api/workEnvironmentTypes';
import { areaLeftRows } from '../../WorkEnvironmentSurveyDetail/areasLeftView';
import classes from '../WorkEnvironmentDashboardPage.module.css';

const K = 'workEnvironmentDashboard.areasLeft';
const L = 'workEnvironmentAreasLeft';

/**
 * What the inspection report still waits for in the survey: a plan, or for moderate risk a
 * decision, for each area. It is done in step 7 of the same survey, not in a new one.
 */
export const AreasLeftCard = ({ survey }: { survey: TeamSurveySummary }): ReactElement => {
  const { t, i18n } = useTranslation();
  const rows = areaLeftRows(survey.areasLeft, survey, 'team', t, i18n.language);
  return (
    <section aria-labelledby="areas-left-heading" className={classes.card} data-areas-left>
      <div className={classes.titleBlock}>
        <Heading level={2} data-size="xs" id="areas-left-heading">
          {t(`${K}.title`)}
        </Heading>
        <Paragraph data-size="sm" className={classes.subtle}>
          {t(`${K}.${survey.canPlan ? 'intro' : 'introViewer'}`, { title: survey.title })}
        </Paragraph>
      </div>
      <ul aria-label={t(`${K}.listLabel`)} className={classes.areasLeft}>
        {rows.map((row) => (
          <li key={row.key} className={classes.areaLeft}>
            <Paragraph data-size="sm">
              {row.overdue && (
                <>
                  <Tag data-size="sm" data-color="danger">
                    {t(`${L}.deviation`)}
                  </Tag>{' '}
                </>
              )}
              {row.text}
            </Paragraph>
            {row.plan && (
              <div className={classes.actions}>
                {/* The survey card has the one primary button of the page */}
                <Button asChild variant="secondary" data-size="sm">
                  <RouterLink to={row.plan} aria-label={t(`${L}.planFor`, { name: row.name })}>
                    {t(`${L}.plan`)}
                  </RouterLink>
                </Button>
                {row.decision && (
                  <Button asChild variant="tertiary" data-size="sm">
                    <RouterLink to={row.decision} aria-label={t(`${L}.decideFor`, { name: row.name })}>
                      {t(`${L}.decide`)}
                    </RouterLink>
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};
